import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, Shield, Check, X, Car, Calendar, Gauge, Fuel,
  Zap, Box, Ruler, Weight, Cog, CircleDot, Users, BarChart3,
  Factory, MapPin, FileText, ArrowRight, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const VIN_SUBS = { I: '1', O: '0', Q: '0' };
const sanitizeVin = (raw) => {
  const before = (raw || '').toUpperCase().replace(/\s+/g, '');
  let converted = false;
  const after = before.replace(/[IOQ]/g, (c) => { converted = true; return VIN_SUBS[c]; });
  return { value: after, converted };
};

const VINCheckPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [vinInput, setVinInput] = useState(searchParams.get('vin') || '');
  const [hint, setHint] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('vin')) {
      handleCheck();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { value, converted } = sanitizeVin(e.target.value);
    setVinInput(value.slice(0, 17));
    setHint(converted ? t('vin.autoConverted') : '');
  };

  const handleCheck = async () => {
    const vin = (vinInput || '').toUpperCase().trim();
    if (vin.length !== 17) {
      toast.error(t('vinPage.errLen'));
      return;
    }
    // Client-side validation: VINs never contain I, O, or Q
    const invalidChars = vin.split('').filter(c => 'IOQ'.includes(c));
    if (invalidChars.length > 0) {
      setResult({
        vin,
        valid: false,
        invalid_reason: `${t('vin.invalidReason')} (${[...new Set(invalidChars)].join(', ')})`,
      });
      return;
    }
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
      setResult({ vin, valid: false, invalid_reason: t('vinPage.errInvalid') });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/vin-check`, { vin }, { timeout: 30000 });
      const data = response?.data || { vin, valid: false };
      if (data.valid) {
        // Go straight to the full polished report
        navigate(`/vin-report/${vin}`);
        return;
      }
      setResult(data);
    } catch (err) {
      console.error('VIN check error:', err);
      const statusInfo = err.response?.status ? ` (HTTP ${err.response.status})` : '';
      const reason = err.response?.data?.detail
        || (err.code === 'ECONNABORTED' ? 'Request timed out. Please try again.' : null)
        || err.message
        || t('vinPage.errNetwork');
      setResult({
        vin,
        valid: false,
        invalid_reason: `${reason}${statusInfo}`,
        network_error: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 sm:py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium mb-4">
            <Shield className="w-3.5 h-3.5" /> {t('vinPage.badge')}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase mb-3">
            <span className="text-cyan-500">{t('vinPage.title1')}</span> {t('vinPage.title2')}
          </h1>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">
            {t('vinPage.subtitle')}
          </p>
        </div>

        {/* Search Box */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="flex gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                placeholder={t('vinPage.placeholder')}
                value={vinInput}
                onChange={handleInputChange}
                maxLength={17}
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
                className="h-14 pl-12 pr-4 text-base font-mono tracking-widest bg-black/50 border-white/10 focus:border-cyan-500/50"
                data-testid="vin-check-input"
              />
            </div>
            <Button
              onClick={handleCheck}
              disabled={loading || vinInput.length !== 17}
              className="h-14 px-6 sm:px-8 bg-cyan-500 text-black hover:bg-cyan-400 font-bold"
              data-testid="vin-check-btn"
            >
              {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <>{t('vin.check')} <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-center gap-2 text-[11px]">
            <span className={`${vinInput.length === 17 ? 'text-green-400' : 'text-gray-600'}`}>{vinInput.length}/17 {t('vinPage.charsCount')}</span>
            {hint && <span className="text-cyan-400" data-testid="vin-hint">· {hint}</span>}
          </div>
        </div>

        {/* Results */}
        {result && (
          <div>
            {!result.valid ? (
              <div className="text-center py-12 rounded-2xl bg-red-500/5 border border-red-500/20" data-testid="vin-invalid-card">
                <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h2 className="font-heading text-xl font-semibold mb-2">
                  {result.network_error ? t('vinPage.connProblem') : t('vinPage.invalidVin')}
                </h2>
                <p className="text-gray-400 text-sm max-w-md mx-auto">
                  {result.invalid_reason || t('vinPage.cantDecode')}
                </p>
                <p className="text-[11px] text-gray-600 mt-3 font-mono">{result.vin}</p>
                {result.network_error && (
                  <button
                    onClick={handleCheck}
                    className="mt-4 px-5 h-9 rounded-md bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs"
                    data-testid="vin-retry-btn"
                  >
                    {t('vinPage.tryAgain')}
                  </button>
                )}
              </div>
            ) : (
              <div>
                {/* Vehicle Header */}
                <div className="rounded-2xl p-5 sm:p-6 bg-white/[0.02] border border-white/5 mb-4">
                  <div className="flex items-start gap-4 flex-wrap">
                    {result.make_logo && (
                      <img src={result.make_logo} alt={result.brand} className="w-14 h-14 object-contain bg-white/10 rounded-xl p-2" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-400 font-medium">{t('vinPage.validVin')}</span>
                      </div>
                      <h2 className="font-heading text-xl sm:text-2xl font-bold">
                        {result.year} {result.brand} {result.model}
                      </h2>
                      <p className="text-gray-500 text-xs mt-1 font-mono">{result.vin}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-gray-400">
                        {result.series && <span>{result.series}</span>}
                        {result.trim && <span className="text-cyan-500">{result.trim}</span>}
                        {result.body_type && <span>{result.body_type}</span>}
                      </div>
                    </div>
                    <Link to={`/vin-report/${result.vin}`} className="shrink-0">
                      <button className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold h-10 px-4 rounded-md text-xs inline-flex items-center gap-1.5" data-testid="vin-full-report-btn">
                        {t('vinPage.viewFullReport')} <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </Link>
                  </div>
                </div>

                {/* Specs Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  {/* Engine & Performance */}
                  <SpecCard title={t('vinPage.engine')} icon={Zap} items={[
                    { label: t('vinPage.specEngine'), value: result.engine },
                    { label: t('vinPage.specDisplacement'), value: result.engine_displacement ? `${result.engine_displacement} cc` : null },
                    { label: t('vinPage.specPower'), value: result.engine_power_hp ? `${result.engine_power_hp} HP (${result.engine_power_kw} kW)` : null },
                    { label: t('vinPage.specCylinders'), value: result.engine_cylinders },
                    { label: t('vinPage.specMaxSpeed'), value: result.max_speed_kmh ? `${result.max_speed_kmh} km/h` : null },
                  ]} />

                  {/* Drivetrain */}
                  <SpecCard title={t('vinPage.drivetrain')} icon={Cog} items={[
                    { label: t('vinPage.specTransmission'), value: result.transmission },
                    { label: t('vinPage.specDriveType'), value: result.drive_type },
                    { label: t('vinPage.specFuelType'), value: result.fuel_type },
                    { label: t('vinPage.specAxles'), value: result.number_of_axles },
                  ]} />

                  {/* Fuel Economy */}
                  <SpecCard title={t('vinPage.fuelConsumption')} icon={Fuel} items={[
                    { label: t('vinPage.specCombined'), value: result.fuel_consumption_combined ? `${result.fuel_consumption_combined} L/100km` : null },
                    { label: t('vinPage.specUrban'), value: result.fuel_consumption_urban ? `${result.fuel_consumption_urban} L/100km` : null },
                    { label: t('vinPage.specExtraUrban'), value: result.fuel_consumption_extra_urban ? `${result.fuel_consumption_extra_urban} L/100km` : null },
                  ]} />

                  {/* Dimensions */}
                  <SpecCard title={t('vinPage.dimensions')} icon={Ruler} items={[
                    { label: t('vinPage.specLength'), value: result.length_mm ? `${result.length_mm} mm` : null },
                    { label: t('vinPage.specWidth'), value: result.width_mm ? `${result.width_mm} mm` : null },
                    { label: t('vinPage.specHeight'), value: result.height_mm ? `${result.height_mm} mm` : null },
                    { label: t('vinPage.specWheelbase'), value: result.wheelbase_mm ? `${result.wheelbase_mm} mm` : null },
                  ]} />

                  {/* Weight */}
                  <SpecCard title={t('vinPage.weightCapacity')} icon={Weight} items={[
                    { label: t('vinPage.specCurbWeight'), value: result.weight_empty_kg ? `${result.weight_empty_kg} kg` : null },
                    { label: t('vinPage.specGrossWeight'), value: result.max_weight_kg ? `${result.max_weight_kg} kg` : null },
                    { label: t('vinPage.specMaxRoofLoad'), value: result.max_roof_load_kg ? `${result.max_roof_load_kg} kg` : null },
                  ]} />

                  {/* Body & Interior */}
                  <SpecCard title={t('vinPage.bodyInterior')} icon={Car} items={[
                    { label: t('vinPage.specBodyType'), value: result.body_type },
                    { label: t('vinPage.specDoors'), value: result.doors },
                    { label: t('vinPage.specSeats'), value: result.seats },
                    { label: t('vinPage.specWheelSize'), value: result.wheel_size },
                  ]} />
                </div>

                {/* Manufacturer */}
                {result.manufacturer && (
                  <div className="rounded-2xl p-4 sm:p-5 bg-white/[0.02] border border-white/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Factory className="w-4 h-4 text-cyan-500" />
                      <h3 className="text-sm font-semibold">{t('vinPage.manufacturer')}</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500 block">{t('vinPage.company')}</span>
                        <span className="text-white font-medium">{result.manufacturer}</span>
                      </div>
                      {result.manufacturer_address && (
                        <div>
                          <span className="text-gray-500 block">{t('vinPage.address')}</span>
                          <span className="text-white font-medium">{result.manufacturer_address}</span>
                        </div>
                      )}
                      {result.plant_country && (
                        <div>
                          <span className="text-gray-500 block">{t('vinPage.country')}</span>
                          <span className="text-white font-medium">{result.plant_country}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Info Section (before check) */}
        {!result && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            {[
              { icon: FileText, title: t('vinPage.fullSpecs'), desc: t('vinPage.fullSpecsDesc') },
              { icon: Factory, title: t('vinPage.manuTitle'), desc: t('vinPage.manuDesc') },
              { icon: Shield, title: t('vinPage.dbTitle'), desc: t('vinPage.dbDesc') },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-5 bg-white/[0.02] border border-white/5">
                <item.icon className="w-6 h-6 text-cyan-500 mb-3" />
                <h3 className="text-sm font-semibold mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* Reusable Spec Card */
const SpecCard = ({ title, icon: Icon, items }) => {
  const validItems = items.filter(i => i.value !== null && i.value !== undefined);
  if (validItems.length === 0) return null;

  return (
    <div className="rounded-xl p-4 bg-white/[0.02] border border-white/5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-cyan-500" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h3>
      </div>
      <div className="space-y-2">
        {validItems.map((item, i) => (
          <div key={i} className="flex justify-between items-center text-xs">
            <span className="text-gray-500">{item.label}</span>
            <span className="text-white font-medium text-right">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VINCheckPage;
